import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Id } from '../../api/types';

/**
 * 🔴 THE SAME QUERY PARAMETER THE BACKLOG'S FILTER BAR ALREADY WRITES.
 *
 * The project sidebar and the filter bar's "Project" select are two controls over ONE piece of
 * state. Giving the sidebar its own key (`?p=`, or React state) would let the two disagree — the
 * rail highlighting FUND while the list showed TBS — and each control would look correct on its own.
 * ⇒ One key, `?project=<project.id>`, read and written by both.
 */
export const PROJECT_PARAM = 'project';

/**
 * Parameters that describe a position INSIDE a project's ticket list, and are meaningless once the
 * project changes. `?ticket=1042` after switching to FUND opens a TBS ticket in a FUND context, and
 * `?page=7` lands on a page that probably does not exist and renders as "nothing matched".
 */
const PROJECT_SCOPED_PARAMS = ['page', 'ticket', 'expanded'] as const;

/**
 * ⭐ ONLY the project survives a view switch.
 *
 * `?project` is a statement about WHAT you are looking at and belongs to all four views. `page`,
 * `ticket`, `expanded`, `status` and `assignee` are the backlog's own vocabulary — carrying them to
 * /board would hand another view a query string it never agreed to parse.
 */
export function viewSearch(projectId: Id | null): string {
  if (!projectId) return '';
  return `?${new URLSearchParams({ [PROJECT_PARAM]: projectId }).toString()}`;
}

export interface ProjectSelection {
  /** `null` means "all projects" — an explicit choice, not an absence. */
  projectId: Id | null;
  select: (projectId: Id | null) => void;
}

export function useProjectSelection(): ProjectSelection {
  const [params, setParams] = useSearchParams();
  const projectId = params.get(PROJECT_PARAM);

  const select = useCallback(
    (next: Id | null) => {
      const search = new URLSearchParams(params);
      if (next) search.set(PROJECT_PARAM, next);
      else search.delete(PROJECT_PARAM);
      for (const key of PROJECT_SCOPED_PARAMS) search.delete(key);
      /**
       * `replace: true`, matching `useBacklogParams().setFilters`. Changing project is a change of
       * FILTER, and pushing a history entry for each would make Back walk backwards through every
       * project you clicked instead of leaving the view — the exact complaint people have about
       * Back in Jira. The URL is still shareable either way; that is what `?project=` is for.
       */
      setParams(search, { replace: true });
    },
    [params, setParams],
  );

  return { projectId, select };
}
